"use client"
import { Elements, PaymentElement, useElements } from "@stripe/react-stripe-js"
import type { Stripe, StripePaymentElementChangeEvent } from "@stripe/stripe-js"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"

import FormFieldError from "@/components/shared/FormFieldError"
import { Button } from "@/components/ui/button"
import { useStripe } from "@/context/StripeContext"
import { useToast } from "@/hooks/use-toast"
import { useQueryState } from "nuqs"
import { useRouter } from "@/i18n/routing"
import { CustomerRequestDialogQuery } from "./CustomerCart"
import { useCart } from "@/hooks/useCart"
import { ROUTES } from "@/routes"

export default function CustomerWallet({
  setupIntentSecret,
  customerSessionSecret,
}: {
  setupIntentSecret: string
  customerSessionSecret: string
}) {
  const { stripe } = useStripe()
  const paymentStatus = usePaymentStatus()
  return (
    <div>
      {stripe && setupIntentSecret && customerSessionSecret && (
        <Elements
          stripe={stripe}
          options={{
            clientSecret: setupIntentSecret,
            customerSessionClientSecret: customerSessionSecret,
          }}
        >
          <SetupForm stripe={stripe} />
        </Elements>
      )}
    </div>
  )
}

function SetupForm({ stripe }: { stripe: Stripe }) {
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [showSubmitButton, setShowSubmitButton] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return null
    }

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${process.env.NEXT_PUBLIC_APP_URL}${pathname}?${searchParams.toString()}`,
      },
    })

    if (error) {
      setErrorMessage(error.message ?? "Failed to add payment method")
    } else {
      setErrorMessage(null)
    }
  }

  const handleChange = (event: StripePaymentElementChangeEvent) => {
    // If the payment method is set, we don't need to show the submit button
    if (event.value.payment_method?.id) {
      setShowSubmitButton(false)
      setErrorMessage(null)
    } else {
      setShowSubmitButton(true)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{ wallets: { applePay: "auto", googlePay: "auto" }, layout: "accordion" }}
        onChange={handleChange}
      />
      {showSubmitButton && (
        <Button type="submit" className="mt-4 w-full">
          Submit
        </Button>
      )}
      <FormFieldError error={errorMessage} />
    </form>
  )
}

export const CustomerWalletRedirectForPaymentQuery = "customer_wallet_redirect_for_payment"
const usePaymentStatus = () => {
  const { stripe } = useStripe()
  const [message, setMessage] = useState<string | null>(null)
  const { toast } = useToast()
  const router = useRouter()
  const { getCartMerchantId } = useCart()
  const [isRedirectForPayment, setIsRedirectForPayment] = useQueryState(CustomerWalletRedirectForPaymentQuery)
  const [, setCustomerRequestDialog] = useQueryState(CustomerRequestDialogQuery)

  useEffect(() => {
    if (!stripe) {
      return
    }

    // Retrieve the "setup_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    const clientSecret = new URLSearchParams(window.location.search).get("setup_intent_client_secret")
    if (!clientSecret) {
      return
    }

    // Retrieve the SetupIntent
    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      if (!setupIntent) {
        return
      }

      const cartMerchantId = getCartMerchantId()
      switch (setupIntent.status) {
        case "succeeded":
          setMessage("Success!")
          toast({
            title: "Success!",
            description: "Your payment method has been saved.",
          })
          if (isRedirectForPayment && cartMerchantId) {
            router.push(`${ROUTES.merchantPublic(cartMerchantId)}?${CustomerRequestDialogQuery}=true`)
          }
          break

        case "processing":
          setMessage("Processing")
          toast({
            title: "Processing",
            description: "We'll update you when processing is complete.",
          })
          break

        case "requires_payment_method":
          setMessage("Failed")
          toast({
            title: "Failed",
            description: "Failed to process payment details. Please try another payment method.",
          })
          break
      }
    })
  }, [stripe])

  return message
}
